import type {FC, PropsWithChildren, ReactNode} from 'react'
import {useEffect} from 'react'
import {useFormContext} from 'react-hook-form'
import {clsx} from 'clsx'
import {logError} from '@/_new-code/services/logger/client'
import {ErrorMessage} from '../form'

interface SelectFieldProps {
	label: ReactNode
	name: string
	className?: string
	isOptional?: boolean
	requiredErrorMsg?: string
	defaultValue?: string
	onChange?: (value: string) => void
	disabled?: boolean
}

export const SelectField: FC<PropsWithChildren<SelectFieldProps>> = ({
	defaultValue = '',
	className = '',
	children,
	label,
	name,
	isOptional = false,
	requiredErrorMsg = '',
	onChange,
	disabled = false,
}) => {
	const requiredMsg = requiredErrorMsg || `${String(label)} is required`
	const {register, trigger, formState} = useFormContext()
	useEffect(() => {
		let ignore = false
		async function validation(): Promise<void> {
			if (defaultValue && !ignore) {
				await trigger(name)
			}
		}

		validation().catch((error) => {
			logError(`SelectField Validation failed: ${error}`)
		})

		return () => {
			ignore = true
		}
	}, [defaultValue, name, trigger])

	const {onChange: libOnChange, ...registerProps} = register(name, {
		required: !isOptional,
	})

	return (
		<div className={clsx('relative mb-4', className)}>
			<label className="w-full text-xs text-pet-primary" htmlFor={name}>
				<span>
					{label}
					{!isOptional && <span className="text-red-600">*</span>}
				</span>
				<div className="relative mt-2">
					<fieldset className="bg-transparent" disabled={disabled}>
						<select
							{...registerProps}
							className={clsx(
								formState.errors[name] && 'invalid',
								'appearance-none rounded-md !bg-white py-3 text-sm font-normal text-pet-primary'
							)}
							defaultValue={defaultValue}
							id={name}
							name={name}
							onChange={(e) => {
								libOnChange(e).catch((error) => {
									logError(
										`SelectField Error in libOnChange: ${error}`
									)
								})
								onChange?.(e.target.value)
							}}
						>
							{children}
						</select>
						<svg
							aria-hidden="true"
							className="mr-[15px] mt-[13px] !fill-mid-grey"
							focusable="false"
							height="20"
							viewBox="0 0 20 20"
							width="20"
						>
							<path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
						</svg>
						{formState.errors[name] ? (
							<ErrorMessage message={requiredMsg} />
						) : null}
					</fieldset>
				</div>
			</label>
		</div>
	)
}
